import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { setPageMeta, SITE_ORIGIN } from '../utils/setPageMeta';

const VISUAL_MOTIVATOR_OG = `${SITE_ORIGIN}/assets/images/visual-motivator-feature-graphic.png`;

export default function VisualMotivator() {
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    setPageMeta({
      title: 'Visual Motivator App | LeadingNG',
      description:
        'Visual Motivator: a simple mobile app for daily motivational quotes, a personal visual board for your goals, reminders, optional Face ID or fingerprint lock, and on-device privacy.',
      canonicalPath: '/app/visual-motivator',
      keywords:
        'Visual Motivator, motivation app, vision board app, daily quotes app, LeadingNG, React Native, goal tracking',
      ogImage: VISUAL_MOTIVATOR_OG,
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      setScrollProgress(documentHeight > 0 ? (window.scrollY / documentHeight) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }} aria-hidden="true" />

      <section className="page-hero page-hero-app-detail">
        <div className="hero-bg" style={{ transform: `translateY(${scrollY * 0.5}px)` }} />
        <div className="container text-center">
          <img
            src={`${process.env.PUBLIC_URL || ''}/assets/images/visual-motivator-icon.png`}
            alt=""
            className="page-hero-app-icon animate-fade-in"
            width={96}
            height={96}
          />
          <span className="page-hero-label animate-fade-in" style={{ animationDelay: '0.05s' }}>App</span>
          <h1 className="page-title animate-slide-up" style={{ animationDelay: '0.1s' }}>
            Visual Motivator
          </h1>
          <p className="page-intro animate-fade-in" style={{ animationDelay: '0.3s' }}>
            A simple mobile app to stay motivated with daily quotes and a personal visual board.
          </p>
        </div>
      </section>

      <section className="section app-detail-section">
        <div className="container">
          <div className="app-info-block">
            <h2 className="title">What it does</h2>
            <p className="intro">
              Visual Motivator helps you keep your goals in sight. Add images to a personal board,
              get daily motivational quotes, and set reminders so you never forget what matters.
              Optional biometric lock keeps your board private on your device.
            </p>
            <ul className="app-features">
              <li>Daily motivational quotes</li>
              <li>Visual board for your images and goals</li>
              <li>Reminders and notifications</li>
              <li>Optional Face ID / fingerprint lock</li>
              <li>Data stored locally on your device</li>
            </ul>
            <p className="app-links">
              <Link to="/app">← Our apps</Link>
              <span className="app-links-sep"> · </span>
              <Link to="/app/visual-motivator/privacy" className="btn btn-cta btn-cta-secondary">
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </section>

      <style>{`
        .page-hero-app-detail {
          position: relative;
          padding: 88px 20px 64px;
          overflow: hidden;
          color: #fff;
        }
        .page-hero-app-detail .hero-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(160deg, var(--color-bg-dark) 0%, #1a252f 100%);
          z-index: 0;
        }
        .page-hero-app-detail > .container {
          position: relative;
          z-index: 1;
        }
        .page-hero-app-icon {
          width: 96px;
          height: 96px;
          object-fit: contain;
          display: block;
          margin: 0 auto 16px;
          border-radius: var(--radius-sm);
        }
        .page-hero-app-detail .page-hero-label {
          display: block;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--color-primary);
          margin-bottom: 8px;
        }
        .page-hero-app-detail .page-title {
          margin: 0 0 12px;
          font-size: 2rem;
          font-weight: 700;
          letter-spacing: -0.02em;
        }
        .page-hero-app-detail .page-intro {
          margin: 0;
          opacity: 0.92;
          font-size: 1.05rem;
        }
        .animate-fade-in { opacity: 0; animation: vmFadeIn 1s ease-out forwards; }
        .animate-slide-up { opacity: 0; transform: translateY(30px); animation: vmSlideUp 0.8s ease-out forwards; }
        @keyframes vmFadeIn { to { opacity: 1; } }
        @keyframes vmSlideUp { to { opacity: 1; transform: translateY(0); } }
        .scroll-progress-bar {
          position: fixed;
          top: 0;
          left: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          z-index: 9999;
          transition: width 0.1s ease-out;
          box-shadow: 0 0 10px var(--color-primary);
        }
        .app-detail-section {
          background: linear-gradient(180deg, #f5f6f8 0%, #e8eaed 100%);
        }
        .app-info-block { max-width: 640px; margin: 0 auto; }
        .app-info-block .title { color: #1a1a1a; }
        .app-info-block .intro { margin-bottom: 1.25rem; color: var(--color-text-muted); }
        .app-features {
          list-style: none;
          margin: 0 0 1.5rem;
          padding: 0;
        }
        .app-features li {
          position: relative;
          padding-left: 1.25rem;
          margin-bottom: 0.5rem;
        }
        .app-features li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.5em;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--color-primary);
        }
        .app-links { margin-top: 1.5rem; }
        .app-links a:first-child { color: var(--color-primary); text-decoration: none; }
        .app-links a:first-child:hover { text-decoration: underline; }
        .app-links-sep { color: var(--color-text-muted); margin: 0 0.25rem; }
      `}</style>
    </>
  );
}
